import {
  Search, ScanLine, Cpu, Briefcase, Smartphone, ShieldCheck, Palette,
  Accessibility, ArrowLeftRight, Tag, Zap, Star, Receipt, Gauge,
  MousePointerClick, Fingerprint, ShieldAlert, Crown, Brain,
} from "lucide-react";

export const SIM_API = `${process.env.REACT_APP_BACKEND_URL}/api/sim`;

export const ENV_MODES = {
  b2b_saas: { label: "B2B Software", sub: "Enterprise / SaaS", defaultViewport: "desktop_1440" },
  b2c_consumer: { label: "B2C Consumer", sub: "Storefront / Web product", defaultViewport: "mobile_390" },
};

export const VIEWPORTS = {
  desktop_1440: { label: "Desktop", dims: "1440×900", icon: "monitor" },
  tablet_768: { label: "Tablet", dims: "768×1024", icon: "tablet" },
  mobile_390: { label: "Mobile", dims: "390×844", icon: "smartphone" },
};

export const VERDICT = {
  APPROVED: { label: "Approved", color: "#10B981", dot: "bg-[#10B981]", text: "text-[#10B981]", ring: "ring-[#10B981]/40" },
  HESITANT: { label: "Hesitant", color: "#F59E0B", dot: "bg-[#F59E0B]", text: "text-[#F59E0B]", ring: "ring-[#F59E0B]/40" },
  DROPPED_OFF: { label: "Dropped Off", color: "#EF4444", dot: "bg-[#EF4444]", text: "text-[#EF4444]", ring: "ring-[#EF4444]/40" },
};

export const SEVERITY = {
  HIGH: { label: "High", color: "#EF4444", border: "border-l-[#EF4444]", chip: "bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/30" },
  MEDIUM: { label: "Medium", color: "#F59E0B", border: "border-l-[#F59E0B]", chip: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  LOW: { label: "Low", color: "#00F0FF", border: "border-l-[#00F0FF]", chip: "bg-[#00F0FF]/10 text-[#00F0FF] border-[#00F0FF]/30" },
};

export const REGION_TYPES = {
  gaze: { label: "Gaze", color: "0,240,255" },
  tap: { label: "Touch", color: "0,122,255" },
  friction: { label: "Friction", color: "239,68,68" },
};

// agent_id -> presentation metadata
export const AGENT_META = {
  b2b_01_skeptic: { short: "Skeptic", icon: Search, accent: "#00F0FF" },
  b2b_02_skimmer: { short: "Skimmer", icon: ScanLine, accent: "#00F0FF" },
  b2b_03_architect: { short: "Architect", icon: Cpu, accent: "#00F0FF" },
  b2b_04_executive: { short: "Executive", icon: Briefcase, accent: "#00F0FF" },
  b2b_05_mobile_reviewer: { short: "Mobile UX", icon: Smartphone, accent: "#00F0FF" },
  b2b_06_guardian: { short: "Guardian", icon: ShieldCheck, accent: "#00F0FF" },
  b2b_07_aesthetic_critic: { short: "Aesthetic", icon: Palette, accent: "#00F0FF" },
  b2b_08_inclusivity_auditor: { short: "A11y Auditor", icon: Accessibility, accent: "#00F0FF" },
  b2b_09_switcher: { short: "Switcher", icon: ArrowLeftRight, accent: "#00F0FF" },
  b2c_01_bargain_stalker: { short: "Bargain", icon: Tag, accent: "#007AFF" },
  b2c_02_impulse_explorer: { short: "Impulse", icon: Zap, accent: "#007AFF" },
  b2c_03_social_proof_seeker: { short: "Social Proof", icon: Star, accent: "#007AFF" },
  b2c_04_subscription_skeptic: { short: "Sub Skeptic", icon: Receipt, accent: "#007AFF" },
  b2c_05_instant_gratifier: { short: "Instant", icon: Gauge, accent: "#007AFF" },
  b2c_06_distracted_scroller: { short: "Scroller", icon: MousePointerClick, accent: "#007AFF" },
  b2c_07_friction_phobic: { short: "Friction Phobic", icon: Fingerprint, accent: "#007AFF" },
  b2c_08_scam_radar: { short: "Scam Radar", icon: ShieldAlert, accent: "#007AFF" },
  b2c_09_lifestyle_aspirant: { short: "Lifestyle", icon: Crown, accent: "#007AFF" },
};

export const agentMeta = (id) => AGENT_META[id] || { short: "Agent", icon: Brain, accent: "#00F0FF" };

export const SIM_TOKEN_KEY = "sim_session_token";

export function simAuthHeaders() {
  const t = localStorage.getItem(SIM_TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function simHeaders(extra = {}) {
  return { ...extra, ...simAuthHeaders() };
}

export async function createSim(body) {
  const r = await fetch(`${SIM_API}/simulations`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(typeof d.detail === "string" ? d.detail : "Could not start simulation");
    err.code = d.detail;
    err.status = r.status;
    throw err;
  }
  return d;
}
export async function createAppSim({ appFile, screenshot, environmentMode }) {
  const fd = new FormData();
  fd.append("app_file", appFile);
  if (screenshot) fd.append("screenshot", screenshot);
  fd.append("environment_mode", environmentMode);
  const r = await fetch(`${SIM_API}/simulations/app`, {
    method: "POST", credentials: "include",
    headers: simHeaders(), // no Content-Type — the browser sets the multipart boundary
    body: fd,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(typeof d.detail === "string" ? d.detail : "Could not start simulation");
    err.code = d.detail;
    err.status = r.status;
    throw err;
  }
  return d;
}
export async function getSim(id) {
  const r = await fetch(`${SIM_API}/simulations/${id}`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) throw new Error("not found");
  return r.json();
}
export async function getShot(id) {
  const r = await fetch(`${SIM_API}/simulations/${id}/screenshot`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) return null;
  return (await r.json()).image;
}
export async function getRecent() {
  const r = await fetch(`${SIM_API}/recent`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) return [];
  return r.json();
}

// ---- Sim auth (Emergent Google Auth + allowlist) ----
export async function simMe() {
  const r = await fetch(`${SIM_API}/auth/me`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) return null;
  return r.json();
}
export async function simExchangeSession(sessionId) {
  const r = await fetch(`${SIM_API}/auth/session`, {
    method: "POST", credentials: "include", headers: { "X-Session-ID": sessionId },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(typeof d.detail === "string" ? d.detail : "sign-in failed");
    err.code = d.detail;
    throw err;
  }
  if (d.session_token) localStorage.setItem(SIM_TOKEN_KEY, d.session_token);
  return d.user;
}
export async function simLogout() {
  try { await fetch(`${SIM_API}/auth/logout`, { method: "POST", credentials: "include", headers: simHeaders() }); } catch {}
  localStorage.removeItem(SIM_TOKEN_KEY);
}
export function simGoogleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/sim?sim_auth=1";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
export async function getSimUsers() {
  const r = await fetch(`${SIM_API}/admin/users`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) throw new Error("forbidden");
  return r.json();
}
export async function setUserAdmin(userId, value) {
  const r = await fetch(`${SIM_API}/admin/users/${encodeURIComponent(userId)}/admin`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ value }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not update admin rights");
  return d;
}
export async function setUserBlocked(userId, value) {
  const r = await fetch(`${SIM_API}/admin/users/${encodeURIComponent(userId)}/block`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ value }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not update access");
  return d;
}

// ---- Sim billing (Stripe: 3 free sims, then $99/mo on a card collected at first login) ----
export async function getBilling() {
  const r = await fetch(`${SIM_API}/billing/status`, { credentials: "include", headers: simHeaders() });
  if (!r.ok) throw new Error("billing_unavailable");
  return r.json();
}
export async function startCardSetup(originUrl, planInterval) {
  const r = await fetch(`${SIM_API}/billing/setup-checkout`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ origin_url: originUrl, ...(planInterval ? { plan_interval: planInterval } : {}) }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not start card setup");
  return d;
}
export async function confirmCardSetup(sessionId) {
  const r = await fetch(`${SIM_API}/billing/confirm-setup`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ session_id: sessionId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not confirm the saved card");
  return d;
}
export async function openBillingPortal(originUrl) {
  const r = await fetch(`${SIM_API}/billing/portal`, {
    method: "POST", credentials: "include",
    headers: simHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ origin_url: originUrl }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not open billing portal");
  return d;
}
